import React, {FC, useRef, useState} from 'react';
import styled, {useTheme} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {MacrosDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/macrosDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Pill} from '../../../../../libs/shared/ui/src/components/pill/pill';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {MacrosBaseModel} from '../../../../models/api/macroModel';
import {runMacroAction} from '../../../../store/data/macros/macroActions';
import {MacroPreview} from './macroPreview';

interface MacroDropdownItemProps {
  macro: MacrosBaseModel;
  conversationId: number;
  activityId: number;
  isRecommended?: boolean;
}

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  ${ellipsis()}
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  ${ellipsis()}
`;

const StyledTitleWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const StyledRecommendedPill = styled(Pill)`
  flex-shrink: 0;
  ${flexCenter}
`;

export const MacroDropdownItem: FC<MacroDropdownItemProps> = ({
  macro,
  conversationId,
  activityId,
  isRecommended,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const {trackDataEvent} = useDataEventTracking();

  return (
    <div
      ref={itemRef}
      onMouseEnter={() => setShouldShowPreview(true)}
      onMouseLeave={() => setShouldShowPreview(false)}
    >
      <DropdownItem
        onSelect={() => {
          trackDataEvent(DataEventActionsEnum.SELECT, MacrosDataEventObject.MACRO_LIST, {
            macro_id: macro.id,
          });
          dispatch(runMacroAction(macro, conversationId, activityId));
        }}
      >
        <StyledDropdownItemContent>
          <StyledTitleWrapperDiv>
            <StyledTitleDiv>{macro.name}</StyledTitleDiv>
            {isRecommended && (
              <StyledRecommendedPill
                size={VisualSizesEnum.MEDIUM}
                color={theme.palette.pink.shade50}
                backgroundColor={theme.palette.pink.shade20}
              >
                <Trans id="rule-card-dropdown-item-recommended">Recommended</Trans>
              </StyledRecommendedPill>
            )}
          </StyledTitleWrapperDiv>
          {shouldShowPreview && <MacroPreview anchor={itemRef} popperPlacement="right" macro={macro} />}
          <StyledDescriptionDiv>{macro.description}</StyledDescriptionDiv>
        </StyledDropdownItemContent>
      </DropdownItem>
    </div>
  );
};
